import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  TeamsRequestError,
  type Team,
  type TeamUsageMemberGroup,
  type TeamUsagePeriod,
  type TeamUsageProjectGroup,
} from '../../api/teams'
import { useTeamUsage, useTeamUsagePeriods } from '../../hooks/useTeams'
import { cn } from '../../lib/utils'
import {
  trackPostpaidUsageBreakdownSelected,
  trackPostpaidUsagePeriodSelected,
} from '../../analytics/interactionAnalytics'
import { createNadDebug } from '../../utils/debugLog'

const log = createNadDebug('dw:postpaid-usage')

type Breakdown = 'project' | 'member'
const BREAKDOWNS: readonly Breakdown[] = ['project', 'member']
const CURRENT_PERIOD = 'current'

function inclusiveEndDate(exclusiveEndDate: string): Date {
  const value = new Date(`${exclusiveEndDate}T00:00:00Z`)
  value.setUTCDate(value.getUTCDate() - 1)
  return value
}

function periodValue(period: TeamUsagePeriod): string {
  return period.isClosed && period.usagePeriodId ? period.usagePeriodId : CURRENT_PERIOD
}

function usageRowKey(
  row: TeamUsageProjectGroup | TeamUsageMemberGroup,
  breakdown: Breakdown,
): string {
  if (breakdown === 'project' && 'projectId' in row) return row.projectId ?? row.label
  if ('userId' in row) return row.userId ?? row.label
  return row.label
}

export default function PostpaidUsageSummary({
  team,
  projectId,
}: {
  team: Team
  projectId?: string
}) {
  const { t, i18n } = useTranslation('settings')
  const [breakdown, setBreakdown] = useState<Breakdown>('project')
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null)
  const periods = useTeamUsagePeriods(team.id, true)
  const selectedDescriptor = periods.data?.find(
    (period) => periodValue(period) === selectedPeriod,
  )
  const usagePeriodId =
    selectedDescriptor?.isClosed === true
      ? selectedDescriptor.usagePeriodId ?? undefined
      : undefined
  const usage = useTeamUsage(
    team.id,
    selectedDescriptor !== undefined,
    projectId,
    usagePeriodId,
  )
  const formatNumber = useMemo(
    () => new Intl.NumberFormat(i18n.language),
    [i18n.language],
  )
  const formatDate = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeZone: 'UTC',
      }),
    [i18n.language],
  )

  useEffect(() => {
    setSelectedPeriod(null)
    setBreakdown('project')
  }, [projectId, team.id])

  useEffect(() => {
    if (!periods.data?.length) return
    const values = new Set(periods.data.map(periodValue))
    if (selectedPeriod !== null && values.has(selectedPeriod)) return
    const current = periods.data.find((period) => !period.isClosed)
    setSelectedPeriod(periodValue(current ?? periods.data[0]))
  }, [periods.data, selectedPeriod])

  useEffect(() => {
    log({
      event: 'mode-resolved',
      team_id: team.id,
      project_id: projectId ?? null,
      mode: 'postpaid',
    })
  }, [projectId, team.id])

  useEffect(() => {
    if (!usage.data) return
    log({
      event: 'period-loaded',
      team_id: team.id,
      project_id: projectId ?? null,
      period_start: usage.data.periodStart,
      period_end: usage.data.periodEnd,
      total_credits: usage.data.totalCredits,
    })
  }, [projectId, team.id, usage.data])

  if (periods.isLoading) {
    return (
      <p
        role="status"
        className="text-base text-muted-foreground"
        data-testid="postpaid-usage-loading"
      >
        {t('settingsModal.postpaidUsage.loadingPeriods')}
      </p>
    )
  }
  if (periods.isError) {
    return (
      <div role="alert" className="flex flex-col items-start gap-2 text-base text-destructive" data-testid="postpaid-periods-error">
        <p>{t('settingsModal.postpaidUsage.periodsLoadFailed')}</p>
        <button type="button" onClick={() => void periods.refetch()} className="h-9 rounded-lg border border-input-border bg-surface-1 px-3 text-sm font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20">
          {t('settingsModal.postpaidUsage.retry')}
        </button>
      </div>
    )
  }
  if (!periods.data?.length) {
    return <p className="text-base text-muted-foreground" data-testid="postpaid-periods-empty">{t('settingsModal.postpaidUsage.noPeriods')}</p>
  }

  const historicalProjectEmpty =
    projectId !== undefined &&
    usagePeriodId !== undefined &&
    usage.error instanceof TeamsRequestError &&
    usage.error.code === 'project_not_found'

  const formatRange = (period: Pick<TeamUsagePeriod, 'periodStartDate' | 'periodEndDate'>) => {
    const start = formatDate.format(new Date(`${period.periodStartDate}T00:00:00Z`))
    const end = formatDate.format(inclusiveEndDate(period.periodEndDate))
    return `${start} – ${end}`
  }

  const handlePeriodChange = (value: string) => {
    const descriptor = periods.data?.find((period) => periodValue(period) === value)
    setSelectedPeriod(value)
    if (!descriptor) return
    trackPostpaidUsagePeriodSelected({
      teamId: team.id,
      projectId,
      scope: projectId ? 'project' : 'team',
      periodKind: descriptor.isClosed ? 'historical' : 'current',
      usagePeriodId: descriptor.usagePeriodId ?? undefined,
    })
  }
  const rows = usage.data
    ? breakdown === 'project' ? usage.data.projects : usage.data.members
    : []

  return (
    <section className="flex flex-col gap-5" data-testid="postpaid-usage">
      <div>
        <h4 className="text-lg font-semibold text-foreground" data-testid="postpaid-usage-team-name">{team.name}</h4>
        <p className="text-base text-muted-foreground">
          {t('settingsModal.postpaidUsage.description')}
        </p>
        <label className="mt-3 flex max-w-md flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground">{t('settingsModal.postpaidUsage.periodLabel')}</span>
          <select
            value={selectedPeriod ?? ''}
            onChange={(event) => handlePeriodChange(event.target.value)}
            disabled={usage.isFetching}
            className="h-9 rounded-lg border border-input-border bg-input-bg px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-wait disabled:opacity-60"
            data-testid="postpaid-usage-period-select"
          >
            {periods.data.map((period) => (
              <option key={`${period.contractId}:${periodValue(period)}`} value={periodValue(period)}>
                {period.isClosed
                  ? formatRange(period)
                  : t('settingsModal.postpaidUsage.currentOption', { range: formatRange(period) })}
              </option>
            ))}
          </select>
        </label>
        {selectedDescriptor && (
          <p className="mt-2 text-sm text-text-muted" data-testid="postpaid-usage-period">
            {selectedDescriptor.isClosed
              ? t('settingsModal.postpaidUsage.historicalHelp')
              : t('settingsModal.postpaidUsage.currentHelp')}
          </p>
        )}
      </div>

      {usage.isLoading && <p role="status" className="text-base text-muted-foreground" data-testid="postpaid-usage-loading">{t('settingsModal.postpaidUsage.loading')}</p>}
      {historicalProjectEmpty && <p className="rounded-xl border border-border bg-surface-0 p-4 text-sm text-muted-foreground" data-testid="postpaid-project-no-usage">{t('settingsModal.postpaidUsage.projectNoUsage')}</p>}
      {usage.isError && !historicalProjectEmpty && (
        <div role="alert" className="flex flex-col items-start gap-2 text-base text-destructive" data-testid="postpaid-usage-error">
          <p>{t('settingsModal.postpaidUsage.loadFailed')}</p>
          <button type="button" onClick={() => void usage.refetch()} className="h-9 rounded-lg border border-input-border bg-surface-1 px-3 text-sm font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20">{t('settingsModal.postpaidUsage.retry')}</button>
        </div>
      )}

      {usage.data && !historicalProjectEmpty && (
      <>
        <div className="rounded-xl border border-border bg-surface-0 p-4">
        <p className="text-sm text-muted-foreground">
          {t('settingsModal.postpaidUsage.creditsUsed')}
        </p>
        <p
          className="mt-1 text-hero font-semibold tabular-nums text-foreground"
          data-testid="postpaid-usage-total"
        >
          {formatNumber.format(usage.data.totalCredits)}
        </p>
        </div>

      {!projectId && (
        <div
          className="inline-flex w-fit rounded-lg border border-input-border bg-input-bg p-1"
          role="group"
          aria-label={t('settingsModal.postpaidUsage.breakdown')}
        >
          {BREAKDOWNS.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={breakdown === value}
              data-testid={`postpaid-usage-by-${value}`}
              onClick={() => {
                setBreakdown(value)
                trackPostpaidUsageBreakdownSelected({ teamId: team.id, breakdown: value })
              }}
              className={cn(
                'h-7 rounded-lg px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
                breakdown === value
                  ? 'bg-surface-3 text-foreground'
                  : 'text-muted-foreground hover:bg-surface-2 hover:text-foreground',
              )}
            >
              {t(
                `settingsModal.postpaidUsage.by${value === 'project' ? 'Project' : 'Member'}`,
              )}
            </button>
          ))}
        </div>
      )}

      {!projectId && (
        <div
          className="overflow-hidden rounded-xl border border-border bg-surface-0"
          data-testid="postpaid-usage-breakdown"
        >
          {rows.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              {t('settingsModal.postpaidUsage.noUsage')}
            </p>
          ) : (
            rows.map((row) => (
              <div
                key={`${breakdown}:${usageRowKey(row, breakdown)}`}
                className="flex items-center justify-between gap-4 border-b border-border-subtle px-4 py-3 last:border-b-0"
              >
                <span className="min-w-0 truncate text-sm text-foreground">
                  {row.label}
                </span>
                <span className="shrink-0 text-sm font-medium tabular-nums text-foreground">
                  {formatNumber.format(row.credits)}
                </span>
              </div>
            ))
          )}
        </div>
      )}
      </>
      )}
    </section>
  )
}
